import { FilterOptions, Game, SportType } from '@/types';

export type AnalyticsEntrySource = 'home' | 'blog' | 'direct' | 'external' | 'unknown';
export type AnalyticsFilterType = 'sport' | 'max' | 'min' | 'competition';
export type AnalyticsLinkContext = 'body' | 'cta' | 'related' | 'nav';
export type CountBucket = '0' | '1-5' | '6-25' | '26-100' | '100+';
export type CardPositionBucket = '1' | '2-5' | '6-10' | '11+';
export type GameAgeBucket = '0-1d' | '2-7d' | '8-30d' | '31d+';

type UtmField = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content';
type UtmParams = Partial<Record<UtmField, string>>;

interface SportPageStartEvent extends UtmParams {
  sport: SportType;
  page_path: string;
  game_count_bucket: CountBucket;
  has_games: boolean;
  entry_source: AnalyticsEntrySource;
}

interface FilterChangedEvent {
  sport: SportType;
  filter_type: AnalyticsFilterType;
  max_value_bucket: string;
  min_value_bucket: string;
  competition_count: number;
  result_count_bucket: CountBucket;
}

interface ScoreRevealedEvent {
  sport: SportType;
  card_position_bucket: CardPositionBucket;
  has_recap: boolean;
  game_age_bucket: GameAgeBucket;
}

interface RecapClickedEvent {
  sport: SportType;
  card_position_bucket: CardPositionBucket;
  provider_host: string;
  score_revealed_first: boolean;
  game_age_bucket: GameAgeBucket;
}

interface BlogToSportClickEvent extends UtmParams {
  post_slug: string;
  sport: SportType;
  destination_path: string;
  link_context: AnalyticsLinkContext;
}

interface AnalyticsEventMap {
  sport_page_start: SportPageStartEvent;
  filter_changed: FilterChangedEvent;
  score_revealed: ScoreRevealedEvent;
  recap_clicked: RecapClickedEvent;
  blog_to_sport_click: BlogToSportClickEvent;
}

const FILTER_BUCKETS: Record<SportType, Array<{ max: number; label: string }>> = {
  nba: [
    { max: 3, label: '1-3' },
    { max: 7, label: '4-7' },
    { max: 15, label: '8-15' },
    { max: Number.POSITIVE_INFINITY, label: '16+' },
  ],
  soccer: [
    { max: 1, label: '0-1' },
    { max: 3, label: '2-3' },
    { max: 5, label: '4-5' },
    { max: Number.POSITIVE_INFINITY, label: '6+' },
  ],
  nfl: [
    { max: 3, label: '1-3' },
    { max: 7, label: '4-7' },
    { max: 14, label: '8-14' },
    { max: Number.POSITIVE_INFINITY, label: '15+' },
  ],
  mlb: [
    { max: 1, label: '0-1' },
    { max: 3, label: '2-3' },
    { max: 6, label: '4-6' },
    { max: Number.POSITIVE_INFINITY, label: '7+' },
  ],
  nhl: [
    { max: 1, label: '0-1' },
    { max: 3, label: '2-3' },
    { max: 5, label: '4-5' },
    { max: Number.POSITIVE_INFINITY, label: '6+' },
  ],
};

export function trackAnalyticsEvent<T extends keyof AnalyticsEventMap>(
  eventName: T,
  params: AnalyticsEventMap[T],
  analyticsEnabled: boolean
): boolean {
  if (!analyticsEnabled || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return false;
  }

  window.gtag('event', eventName, params);
  return true;
}

export function getCountBucket(count: number): CountBucket {
  if (count <= 0) {
    return '0';
  }
  if (count <= 5) {
    return '1-5';
  }
  if (count <= 25) {
    return '6-25';
  }
  if (count <= 100) {
    return '26-100';
  }
  return '100+';
}

export function getFilterValueBucket(sport: SportType, value?: number): string {
  if (value === undefined || Number.isNaN(value)) {
    return 'none';
  }

  const bucket = FILTER_BUCKETS[sport].find(candidate => value <= candidate.max);
  return bucket?.label ?? 'unknown';
}

export function getCardPositionBucket(cardIndex: number): CardPositionBucket {
  const cardNumber = cardIndex + 1;

  if (cardNumber <= 1) {
    return '1';
  }
  if (cardNumber <= 5) {
    return '2-5';
  }
  if (cardNumber <= 10) {
    return '6-10';
  }
  return '11+';
}

export function getGameAgeBucket(utcDatetime: string, now = new Date()): GameAgeBucket {
  const gameTime = new Date(utcDatetime);
  const diffInDays = Math.max(0, (now.getTime() - gameTime.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays < 2) {
    return '0-1d';
  }
  if (diffInDays < 8) {
    return '2-7d';
  }
  if (diffInDays < 31) {
    return '8-30d';
  }
  return '31d+';
}

export function getProviderHost(url?: string): string {
  if (!url) {
    return 'unknown';
  }

  try {
    const parsedUrl = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://skore.info');
    return parsedUrl.hostname || 'unknown';
  } catch {
    return 'unknown';
  }
}

export function getUtmParams(search: string): UtmParams {
  const params = new URLSearchParams(search);
  const utmParams: UtmParams = {};

  for (const field of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as UtmField[]) {
    const value = params.get(field);
    if (value) {
      utmParams[field] = value;
    }
  }

  return utmParams;
}

export function getEntrySource(options: {
  locationStateEntrySource?: unknown;
  referrer?: string;
  currentOrigin?: string;
}): AnalyticsEntrySource {
  const { locationStateEntrySource, referrer = '', currentOrigin } = options;

  if (isEntrySource(locationStateEntrySource)) {
    return locationStateEntrySource;
  }

  if (!referrer) {
    return 'direct';
  }

  try {
    const referrerUrl = new URL(referrer);
    const origin = currentOrigin
      ?? (typeof window !== 'undefined' ? window.location.origin : 'https://skore.info');

    if (referrerUrl.origin !== origin) {
      return 'external';
    }

    if (referrerUrl.pathname === '/') {
      return 'home';
    }

    if (referrerUrl.pathname.startsWith('/blog')) {
      return 'blog';
    }
  } catch {
    return 'unknown';
  }

  return 'unknown';
}

export function getVisibleGames(games: Game[]): Game[] {
  const now = Date.now();

  return games.filter(game => {
    if (!game.utcDatetime) {
      return false;
    }

    return new Date(game.utcDatetime).getTime() <= now;
  });
}

export function buildFilterChangedEvent(params: {
  filterType: AnalyticsFilterType;
  filters: FilterOptions;
  visibleResultCount: number;
}): FilterChangedEvent {
  const { filterType, filters, visibleResultCount } = params;

  return {
    sport: filters.sportType,
    filter_type: filterType,
    max_value_bucket: getFilterValueBucket(filters.sportType, filters.maxValue),
    min_value_bucket: getFilterValueBucket(filters.sportType, filters.minValue),
    competition_count: filters.competitions?.length ?? 0,
    result_count_bucket: getCountBucket(visibleResultCount),
  };
}

export function getSportRouteDetails(href: string, origin?: string): {
  sport: SportType;
  destinationPath: string;
  utmParams: UtmParams;
} | null {
  try {
    const baseOrigin = origin
      ?? (typeof window !== 'undefined' ? window.location.origin : 'https://skore.info');
    const url = new URL(href, baseOrigin);
    const match = url.pathname.match(/^\/sport\/(nba|soccer|nfl|mlb|nhl)(?:\/|$)/);

    if (!match || url.origin !== baseOrigin) {
      return null;
    }

    return {
      sport: match[1] as SportType,
      destinationPath: `${url.pathname}${url.search}${url.hash}`,
      utmParams: getUtmParams(url.search),
    };
  } catch {
    return null;
  }
}

function isEntrySource(value: unknown): value is AnalyticsEntrySource {
  return value === 'home'
    || value === 'blog'
    || value === 'direct'
    || value === 'external'
    || value === 'unknown';
}

declare global {
  interface Window {
    gtag?: (command: string, action: string, params?: Record<string, unknown>) => void;
  }
}
